/**
 * Enhanced security enforcement utilities
 */

import { SECURITY_CONFIG, getSecurityConfig } from './securityConfig';
import { SecurityMonitor } from './securityMonitoring';

// Enhanced input validation with security checks
export function validateAndSanitizeInput(
  input: string, 
  fieldName: string, 
  maxLength?: number
): { isValid: boolean; sanitized: string; error?: string } {
  try {
    // Check for dangerous patterns
    const dangerous = SECURITY_CONFIG.CONTENT_SECURITY.DANGEROUS_PATTERNS.some(
      pattern => pattern.test(input)
    );
    
    if (dangerous) {
      SecurityMonitor.logSecurityViolation('dangerous_input_detected', 'form_validation', {
        fieldName,
        inputLength: input.length
      });
      return {
        isValid: false,
        sanitized: '',
        error: 'Contenu non autorisé détecté'
      };
    }

    // Length validation
    const limit = maxLength || SECURITY_CONFIG.CONTENT_SECURITY.MAX_STRING_LENGTH;
    if (input.length > limit) {
      return {
        isValid: false,
        sanitized: '',
        error: `Texte trop long (maximum ${limit} caractères)`
      };
    }

    // Basic HTML stripping and sanitization
    const sanitized = input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+=/gi, '')
      .trim();

    return {
      isValid: true,
      sanitized
    };
  } catch (error) {
    SecurityMonitor.logSecurityViolation('input_validation_error', 'form_validation', {
      fieldName,
      error: error instanceof Error ? error.message : 'Unknown error'
    });
    return {
      isValid: false,
      sanitized: '',
      error: 'Erreur de validation'
    };
  }
}

// Enhanced email validation with security checks
export function validateEmail(email: string): { isValid: boolean; error?: string } {
  // Length check
  if (email.length > SECURITY_CONFIG.FORM_LIMITS.EMAIL_MAX) {
    return {
      isValid: false,
      error: `Email trop long (maximum ${SECURITY_CONFIG.FORM_LIMITS.EMAIL_MAX} caractères)`
    };
  }

  // Pattern validation
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(email)) {
    return {
      isValid: false,
      error: 'Format d\'email invalide'
    };
  }

  // Security validation
  const { isValid, error } = validateAndSanitizeInput(email, 'email');
  return { isValid, error };
}

// Phone number validation with international support
export function validatePhone(phone: string): { isValid: boolean; error?: string } {
  if (phone.length > SECURITY_CONFIG.FORM_LIMITS.PHONE_MAX) {
    return {
      isValid: false,
      error: `Numéro trop long (maximum ${SECURITY_CONFIG.FORM_LIMITS.PHONE_MAX} caractères)`
    };
  }

  // Allow French and international formats
  const phoneRegex = /^(\+33|0)[1-9](\d{8})$|^\+\d{8,15}$/;
  const cleanPhone = phone.replace(/[\s.-]/g, '');
  
  if (!phoneRegex.test(cleanPhone)) {
    return {
      isValid: false,
      error: 'Format de téléphone invalide'
    };
  }

  return { isValid: true };
}

// Request origin validation
export function validateRequestOrigin(origin: string | null): boolean {
  if (!origin) return false;
  
  const allowedOrigins = [
    window.location.origin,
    'https://assurancedo.fr',
    'https://www.assurancedo.fr',
    ...SECURITY_CONFIG.ENVIRONMENT.DEVELOPMENT.ENABLE_CONSOLE_LOGGING 
      ? ['http://localhost:8080', 'http://127.0.0.1:8080'] 
      : []
  ];
  
  return allowedOrigins.includes(origin);
}

// Enhanced request validation
export function validateRequest(request: {
  headers?: Record<string, string>;
  body?: any;
  origin?: string;
}): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Validate origin
  if (request.origin && !validateRequestOrigin(request.origin)) {
    errors.push('Origine de requête non autorisée');
    SecurityMonitor.logSecurityViolation('invalid_origin', 'request_validation', {
      origin: request.origin
    });
  }

  // Validate content type for POST requests
  if (request.body && request.headers) {
    const contentType = request.headers['content-type'] || request.headers['Content-Type'];
    if (contentType && !contentType.includes('application/json')) {
      errors.push('Type de contenu non autorisé');
    }
  }

  // Validate request size
  if (request.body) {
    const bodySize = JSON.stringify(request.body).length;
    if (bodySize > 100000) { // 100KB limit
      errors.push('Taille de requête trop importante');
      SecurityMonitor.logSecurityViolation('oversized_request', 'request_validation', {
        size: bodySize
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

// File upload validation
export function validateFileUpload(file: File): { isValid: boolean; error?: string } {
  // Size limit: 5MB
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'Fichier trop volumineux (maximum 5MB)'
    };
  }

  // Allowed types
  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Type de fichier non autorisé'
    };
  }

  // File name validation
  const dangerousExtensions = /\.(exe|bat|cmd|scr|vbs|js|jar|com|pif)$/i;
  if (dangerousExtensions.test(file.name)) {
    SecurityMonitor.logSecurityViolation('dangerous_file_upload', 'file_validation', {
      fileName: file.name,
      fileType: file.type
    });
    return {
      isValid: false,
      error: 'Extension de fichier non autorisée'
    };
  }

  return { isValid: true };
}

// Secure headers for API requests
export function getSecureHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...SECURITY_CONFIG.SECURITY_HEADERS
  };
}

// Initialize security enforcement
export function initializeSecurity() {
  if (typeof window !== 'undefined') {
    // Log security initialization
    SecurityMonitor.logEvent('info', 'security_initialized', 'app_startup');

    // Monitor for suspicious activity
    const suspiciousEvents = ['contextmenu', 'selectstart', 'copy'];
    const handleSuspiciousActivity = (event: Event) => {
      if (process.env.NODE_ENV === 'production') {
        SecurityMonitor.logEvent('warning', 'suspicious_activity', 'user_interaction', {
          eventType: event.type,
          timestamp: Date.now()
        });
      }
    };

    suspiciousEvents.forEach(eventType => {
      document.addEventListener(eventType, handleSuspiciousActivity);
    });

    // Validate environment security
    const config = getSecurityConfig();
    if (!config.IS_PRODUCTION) {
      console.warn('[Security] Running in development mode - security features may be relaxed');
    }

    return () => {
      suspiciousEvents.forEach(eventType => {
        document.removeEventListener(eventType, handleSuspiciousActivity);
      });
    };
  }
  return () => {};
}